import { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
} from "@mui/material";
import api from '../config/api';
import { useTranslation } from 'react-i18next';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "80%", md: "60%", lg: "50%" },
  maxWidth: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const UserProjectsModal = ({
  open,
  onClose,
  user,
  authToken,
  organizationId,
  showAlert,
  setCounter,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  
  const { t } = useTranslation();

  // Cargar proyectos de la organización
  useEffect(() => {
    if (open && organizationId && authToken) {
      loadOrganizationProjects();
    }
  }, [open, organizationId, authToken]);

  // Establecer proyectos seleccionados cuando cambia el usuario
  useEffect(() => {
    if (user && projects.length > 0) {
      setSelectedProjects(user.projects || []);
    }
  }, [user, projects]);

  const loadOrganizationProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(
        `/v2/projects/${organizationId}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.response?.message === "All projects were successfully obtained") {
        setProjects(response.data.response.data);
      } else {
        throw new Error("Error al cargar proyectos");
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      showAlert(t('users.projectsLoadError'), "error");
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Llamar a la API para asignar proyectos al usuario
      await api.put(
        `/projects/assignProjects/${user.uid}?organizationId=${organizationId}`,
        { projects: selectedProjects },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      showAlert(t('users.assignProjectsSuccess'), "success");
      onClose();
      setCounter?.(prev => prev + 1);
    } catch (error) {
      console.error("Error assigning projects:", error);
      showAlert(t('users.assignProjectsError'), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset estado
      setSelectedProjects([]);
    }
  };

  if (!user) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ textAlign: "center", mb: 3 }}>
          {t('users.assignProjects')}
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('user')}:</strong> {user.name} ({user.email})
          </Typography>
        </Alert>

        <Divider sx={{ mb: 3 }} />

        {loadingProjects ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : projects.length === 0 ? (
          <Alert severity="warning">
            <Typography variant="body2">
              {t('users.noProjectsInOrganization')}
            </Typography>
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {t('users.selectProjects')}:
            </Typography>
            
            <FormGroup>
              {projects.map((project) => (
                <FormControlLabel
                  key={project.uid}
                  control={
                    <Checkbox
                      checked={selectedProjects.includes(project.uid)}
                      onChange={() => handleProjectToggle(project.uid)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {project.name}
                      </Typography>
                      {project.description && (
                        <Typography variant="caption" color="text.secondary">
                          {project.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </FormGroup>

            <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('users.selectedProjects')}:</strong> {selectedProjects.length}
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || loadingProjects || projects.length === 0}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('processing')}
              </>
            ) : (
              t('save')
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserProjectsModal;